// /src/components/tools/memberProfileCard/PerkThemeOverlayEffects.jsx

import React, { useMemo } from 'react';
import DonorShootingStar from './DonorShootingStar';
import PerkThemeLightningCanvas from './PerkThemeLightningCanvas';
import PerkThemeGlassDropsCanvas from './PerkThemeGlassDropsCanvas';
import PerkThemeHeatCanvas from './PerkThemeHeatCanvas';
import PerkThemeNebulaCanvas from './PerkThemeNebulaCanvas';
import PerkThemeSnowCanvas from './PerkThemeSnowCanvas';
import { normalizeEffectConfig } from '../../../utils/memberProfileThemeDisplay';

const RAIN_FRONT_MAX = 110;
const RAIN_BACK_MAX = 75;

function clampDensity(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 100;
  return Math.max(0, Math.min(100, Math.round(n)));
}

/** Scale drop count only — same CSS stem classes / timing / look. */
function rainDropCount(max, density) {
  const d = clampDensity(density);
  // 0 → ~10% of max, 100 → full preset (current look)
  const t = 0.1 + 0.9 * (d / 100);
  return Math.max(1, Math.round(max * t));
}

/** Falling rain streaks (fine dense stems). */
function FallingRainLayer({ density = 100 }) {
  const frontCount = rainDropCount(RAIN_FRONT_MAX, density);
  const backCount = rainDropCount(RAIN_BACK_MAX, density);
  const frontIndexes = useMemo(
    () => Array.from({ length: frontCount }, (_, i) => i),
    [frontCount],
  );
  const backIndexes = useMemo(
    () => Array.from({ length: backCount }, (_, i) => i),
    [backCount],
  );

  return (
    <div className="perk-theme-overlay__layer perk-theme-overlay__layer--rain" aria-hidden>
      <div className="perk-theme-overlay__rain-row perk-theme-overlay__rain-row--back">
        {backIndexes.map((i) => (
          <div
            key={`back-${i}`}
            className={`perk-theme-overlay__rain-drop perk-theme-overlay__rain-drop--back-${i}`}
          >
            <span className="perk-theme-overlay__rain-stem" />
          </div>
        ))}
      </div>
      <div className="perk-theme-overlay__rain-row perk-theme-overlay__rain-row--front">
        {frontIndexes.map((i) => (
          <div
            key={`front-${i}`}
            className={`perk-theme-overlay__rain-drop perk-theme-overlay__rain-drop--front-${i}`}
          >
            <span className="perk-theme-overlay__rain-stem" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Stackable overlay FX for media-backed themes (never with legacy effectKey packs).
 * @param {{ effectKeys?: string[], mediaUrl?: string, mediaType?: string, effectConfig?: object }} props
 */
export default function PerkThemeOverlayEffects({
  effectKeys,
  mediaUrl = '',
  mediaType = '',
  effectConfig = null,
}) {
  const keys = Array.isArray(effectKeys) ? effectKeys : [];
  // starfield is an underlay (behind media), not a front overlay
  const frontKeys = keys.filter((k) => k !== 'starfield');
  if (frontKeys.length === 0) return null;

  const cfg = normalizeEffectConfig(effectConfig);
  const showRain = frontKeys.includes('rain');
  const showSnow = frontKeys.includes('snow');
  const showDrip = frontKeys.includes('drip') || frontKeys.includes('radioactive-drip');
  const dripRadioactive = frontKeys.includes('radioactive-drip');
  const showLightning = frontKeys.includes('lightning');
  const showStars = frontKeys.includes('shooting-stars');
  const showNebula = frontKeys.includes('nebula');
  const showHeat = frontKeys.includes('heat');

  return (
    <div className="perk-theme-overlay" aria-hidden>
      {showNebula ? (
        <PerkThemeNebulaCanvas
          quantity={cfg.nebulaQuantity}
          inclination={cfg.nebulaInclination}
          motion={cfg.nebulaMotion}
        />
      ) : null}
      {showHeat ? (
        <PerkThemeHeatCanvas
          mediaUrl={mediaUrl}
          mediaType={mediaType}
          intensity={cfg.heatIntensity}
        />
      ) : null}
      {showDrip ? (
        <PerkThemeGlassDropsCanvas
          key={dripRadioactive ? 'radioactive-drip' : 'drip'}
          mediaUrl={mediaUrl}
          mediaType={mediaType}
          radioactive={dripRadioactive}
          density={cfg.dripDensity}
        />
      ) : null}
      {showRain ? <FallingRainLayer density={cfg.rainDensity} /> : null}
      {showSnow ? (
        <PerkThemeSnowCanvas
          density={cfg.snowDensity}
          wind={cfg.snowWind}
        />
      ) : null}
      {showLightning ? (
        <PerkThemeLightningCanvas
          quantity={cfg.lightningQuantity}
          height={cfg.lightningHeight}
        />
      ) : null}
      {showStars ? (
        <DonorShootingStar quantity={cfg.shootingStarsQuantity} />
      ) : null}
    </div>
  );
}
